import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, UploadSimple, FileCsv, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { api, formatApiErrorDetail } from "@/lib/api";

const COLUMNS = [
  "code",
  "door_style",
  "category",
  "name",
  "width_in",
  "height_in",
  "depth_in",
  "box_color",
  "hinge",
  "price",
  "qty",
  "low_stock",
  "notes",
];

export default function CabinetImport() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/cabinets/import/preview", fd);
      setPreview(data);
      setFileName(file.name);
      toast.success(`${data.count} row${data.count === 1 ? "" : "s"} ready — review below`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read that file");
    }
    setBusy(false);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cabinets/import", { rows: preview.rows });
      toast.success(
        `${data.created} cabinet${data.created === 1 ? "" : "s"} added${
          data.skipped?.length ? ` · ${data.skipped.length} skipped` : ""
        }`,
      );
      navigate("/inventory/cabinetry");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    }
    setBusy(false);
  };

  const template = () => {
    const blob = new Blob(
      [
        `${COLUMNS.join(",")}\nSW-0001,Shaker White,Base,24" Base Cabinet,24,34.5,24,White,N,189.00,10,2,Soft-close hinges\n`,
      ],
      { type: "text/csv" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cabinet-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Cabinetry / Import" />
      <main className="relative z-10 mx-auto max-w-5xl px-4 pb-28 pt-6">
        <Link
          to="/inventory/cabinetry"
          data-testid="back-to-cabinetry"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> Cabinetry
        </Link>
        <h1 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
          Import a cabinet line
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Upload a CSV of a whole cabinet line. Every row is checked and shown for review
          before anything is saved — unknown door styles are rejected so you can add them first.
        </p>

        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-6">
          <div className="rounded-lg border border-dashed border-zinc-700 p-10 text-center">
            <UploadSimple size={30} weight="bold" className="mx-auto text-zinc-500" />
            <div className="mt-3 text-base font-semibold">Drop your cabinet list here</div>
            <div className="mt-1 text-xs text-zinc-500">CSV with a header row</div>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Button
                data-testid="choose-cabinet-file"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
                className="h-11 bg-indigo-600 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-500"
              >
                <FileCsv size={16} weight="bold" className="mr-2" />
                {busy ? "Reading…" : "Choose file"}
              </Button>
              <Button
                data-testid="download-cabinet-template"
                variant="outline"
                onClick={template}
                className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
              >
                Download template
              </Button>
            </div>
            <input
              ref={inputRef}
              data-testid="cabinet-import-input"
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={pick}
            />
          </div>
          <p className="mt-4 flex items-start gap-2 text-xs text-zinc-500">
            <Sparkle size={14} weight="bold" className="mt-0.5 shrink-0 text-indigo-300" />
            Columns: {COLUMNS.join(", ")}. Headers are matched by name, extra columns are ignored.
            A <span className="text-zinc-300">code</span> is required on every row; a door style must
            already exist in your Door styles list.
          </p>
        </div>

        {preview && (
          <div className="mt-6" data-testid="cabinet-import-preview">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">{fileName}</div>
                <div className="text-sm text-zinc-300">
                  <span className="font-mono-data text-emerald-400">{preview.count}</span> ready to
                  import
                  {preview.errors.length > 0 && (
                    <>
                      {" · "}
                      <span className="font-mono-data text-red-400">{preview.errors.length}</span>{" "}
                      skipped
                    </>
                  )}
                </div>
              </div>
              <Button
                data-testid="confirm-cabinet-import"
                disabled={busy || preview.count === 0}
                onClick={confirm}
                className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                {busy ? "Importing…" : `Import ${preview.count} cabinets`}
              </Button>
            </div>

            {preview.errors.length > 0 && (
              <div
                className="mt-3 rounded-lg border border-red-500/30 bg-red-500/5 p-4"
                data-testid="cabinet-import-errors"
              >
                {preview.errors.slice(0, 20).map((e) => (
                  <div key={e} className="text-xs text-red-300">
                    {e}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-900">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="p-3">Row</th>
                    <th className="p-3">Code</th>
                    <th className="p-3">Name</th>
                    <th className="p-3">Door style</th>
                    <th className="p-3">Category</th>
                    <th className="p-3">Size</th>
                    <th className="p-3 text-right">Price</th>
                    <th className="p-3 text-right">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr
                      key={r._row}
                      data-testid={`cabinet-import-row-${r._row}`}
                      className="border-b border-zinc-800 last:border-0"
                    >
                      <td className="font-mono-data p-3 text-xs text-zinc-500">{r._row}</td>
                      <td className="font-mono-data p-3 font-semibold text-indigo-300">{r.code}</td>
                      <td className="p-3">{r.name || "—"}</td>
                      <td className="p-3 text-zinc-400">{r.door_style || "—"}</td>
                      <td className="p-3 text-zinc-400">{r.category}</td>
                      <td className="font-mono-data p-3 text-zinc-400">
                        {r.width_in || "?"}×{r.height_in || "?"}×{r.depth_in || "?"}"
                      </td>
                      <td className="font-mono-data p-3 text-right text-yellow-400">
                        {r.price != null ? `$${r.price}` : "—"}
                      </td>
                      <td className="font-mono-data p-3 text-right text-zinc-300">{r.qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
